import SearchResultsIcon from '@mui/icons-material/Reorder';
import SearchIcon from '@mui/icons-material/Search';
import ViewIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/clientEditStyle.js';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import Button from 'components/CustomButtons/Button.js';
import CustomInput from 'components/CustomInput/CustomInput';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Table from 'components/Table/Table';
import { useState } from 'react';
import ParameterVersion from './ParameterVersion';
import { useLazySearchParameterHistoryQuery } from 'api/parameter.api';
import { useLazyGetParameterVersionQuery } from 'api/parameter.api';

const useStyles = makeStyles(styles);

export default function ParameterHistory() {
  const [triggerSearchParameterHistory, {data: historyResults}] = useLazySearchParameterHistoryQuery();
  const [triggerGetParameterVersion, {data: currentVersion, isFetching, isSuccess}] = useLazyGetParameterVersionQuery();
  const history = historyResults?.[0]?.data;
  const resultCount = historyResults?.[0]?.metadata?.[0]?.total;
  const versionStatus = isFetching ? 'pending' : isSuccess ? 'success' : 'failed';

  const classes = useStyles();

  const [viewParameterVersion, setViewParameterVersion] = useState(false);
  const [dirtyData, setDirtyData] = useState(false);
  const [search, setSearch] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [form, setForm] = useState({
    name: '',
    type: '',
    startDate: '',
    startDateState: '',
    endDate: '',
    endDateState: '',
  });

  const onClickSearch = async () => {
    setSearch(true);
    await searchParameterHistory();
  };

  const handleViewHistory = async (index) => {
    await triggerGetParameterVersion(history[index]._id).unwrap();
    setViewParameterVersion(true);
  };

  const searchParameterHistory = async (pg, rows) => {
    const searchCriteria = {};
    if (form.name) searchCriteria.name = form.name;
    if (form.type) searchCriteria.type = form.type;
    if (form.startDate) searchCriteria.beginDate = form.startDate;
    if (form.endDate) searchCriteria.endDate = form.endDate;
    searchCriteria.rowsPerPage = rows ? rows : rowsPerPage;
    searchCriteria.page = pg ? pg : page;

    await triggerSearchParameterHistory({criteria: searchCriteria});
    setDirtyData(false);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage + 1);
    setDirtyData(true);
    await searchParameterHistory(newPage + 1);
  };

  const handleChangeRowsPerPage = async (event) => {
    const rowsPP = parseInt(event.target.value, 10);
    setRowsPerPage(rowsPP);
    setResultPageLimit(rowsPP);
    setPage(1);
    setDirtyData(true);
    await searchParameterHistory(1, rowsPP);
  };

  const handleCloseParameterVersion = () => {
    setViewParameterVersion(false);
  };

  const actionButton = (Icon, color, handler, key) => {
    return (
      <Button
        color={color}
        simple
        className={classes.actionButton}
        onClick={() => handler(key)}
      >
        <Icon className={classes.icon} />
      </Button>
    );
  };

  const searchResults = () => {
    let results = [];
    if (history)
      results = history.map((parameter, key) => {
        const result = [];
        const actionButtons = [];

        result.push(parameter.name);
        result.push(parameter.description);
        result.push(parameter.type);
        result.push(
          new Date(parameter.created_at).toLocaleString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })
        );
        result.push(
          parameter.isCurrent ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {parameter.version}{' '}
              <Chip sx={{ marginLeft: 1 }} size='small' label='Current' />
            </Box>
          ) : !parameter.currentParameter.length ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {parameter.version}{' '}
              <Chip
                sx={{ marginLeft: 1 }}
                size='small'
                label='Deleted'
                color='error'
              />
            </Box>
          ) : (
            parameter.version
          )
        );
        actionButtons.push(
          actionButton(ViewIcon, 'warning', handleViewHistory, key)
        );
        result.push(actionButtons);

        return result;
      });

    return results;
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color='rose' icon>
            <CardIcon color='rose'>
              <SearchIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Search Criteria</h4>
          </CardHeader>
          <CardBody>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12}>
                <CustomInput
                  labelText='Name'
                  id='name'
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    value: form.name,
                    onChange: (e) => {
                      setForm({ ...form, name: e.target.value });
                      setDirtyData(true);
                    },
                  }}
                />
                <FormControl
                  variant='standard'
                  className={classes.formControl}
                  fullWidth={true}
                >
                  <InputLabel
                    id='type-select-label'
                    className={classes.inputLabel}
                  >
                    Parameter Types
                  </InputLabel>
                  <Select
                    variant='standard'
                    className={classes.select}
                    labelId='type-select-label'
                    id='type-select'
                    value={form.type}
                    onChange={(e) => {
                      setForm({ ...form, type: e.target.value });
                      setDirtyData(true);
                    }}
                  >
                    <MenuItem value=''>-Empty-</MenuItem>
                    <MenuItem value='parameter'>Parameter</MenuItem>
                    <MenuItem value='template'>Template</MenuItem>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12}>
                <Button
                  color='rose'
                  onClick={onClickSearch}
                  disabled={!dirtyData}
                  className={classes.registerButton}
                >
                  Search
                </Button>
              </GridItem>
            </GridContainer>
          </CardBody>
        </Card>
      </GridItem>
      {history?.length > 0 ? (
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color='rose' icon>
              <CardIcon color='rose'>
                <SearchResultsIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>History</h4>
            </CardHeader>
            <CardBody>
              <Table
                tableHead={[
                  'Set Name',
                  'Set Description',
                  'Type',
                  'Date Changed',
                  'Version',
                  'Actions',
                ]}
                tableData={searchResults()}
              />
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={resultCount}
                page={page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                slotProps={{
                  select: {
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  },
                }}
              />
            </CardBody>
          </Card>
        </GridItem>
      ) : search ? (
        <GridContainer alignItems='center' justify='center'>
          <GridItem xs={12}>
            <Card>
              <CardHeader color='rose' icon>
                <CardIcon color='rose'>
                  <SearchResultsIcon />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>History</h4>
              </CardHeader>
              <CardBody alignItems='center' align='center'>
                <b>No records to display</b>
              </CardBody>
            </Card>
          </GridItem>
        </GridContainer>
      ) : null}
      {viewParameterVersion && versionStatus === 'success' ? (
        <ParameterVersion
          open={viewParameterVersion && currentVersion}
          handleClose={handleCloseParameterVersion}
          version={currentVersion}
          search={onClickSearch}
        />
      ) : null}
    </GridContainer>
  );
}
